/**
 * refine-composer — the shared edit-in-place RLHF Feedback composer.
 *
 * Lifted from `career-werte-sektion` (its `block()`/`refine()` composer, the one the
 * operator praised) so that more than one surface can drive the identical
 * »sag-uns-was-fehlt → Vorschlag → HITL-accept« interaction instead of each surface
 * reinventing it. This is a PURE render helper (a function returning a `TemplateResult`
 * over web-kit `html`), NOT a custom element: the surface owns the state (which target is
 * open, the busy flag, any text proposal) and the VM methods; this file owns only the
 * geometry and the canonical seam input.
 *
 * Three states, selected from {@link RefineComposerState}:
 *   1. closed  → a barely-visible hover affordance (`data-act = acts.open`);
 *   2. open, no proposal → the feedback textarea (the canonical `.rlhf-seam`
 *      `seamAutoGrow()` input — never a fixed-rows textarea) + submit/cancel;
 *   3. open, with a proposal → the AI proposal + accept/again/discard (HITL — the
 *      original is never silently replaced).
 *
 * A surface that re-runs a whole derivation on feedback (rather than proposing a single
 * replacement text) simply keeps `proposal` permanently `null`, so state 3 never renders —
 * `career-search-profile` drives it that way; `career-werte-sektion` COULD adopt it with
 * state 3 live (a later pass — this task does not migrate it).
 *
 * Styling reuses the existing `.ws-afford`/`.ws-iconbtn`/`.ws-refine`/`.ws-btn-sm`/
 * `.ws-proposal` classes in `index.html`; the feedback textarea is the canonical
 * `.rlhf-seam` seam. §0 seamless document: the affordance is a hairline pill, the composer
 * the one earned containment (an active input).
 */

import { classMap, html, nothing, type TemplateResult } from "@platform/web-kit/render"
import { seamAutoGrow } from "@platform/rlhf-elements"

/** The `data-act` verbs each control dispatches — parameterized so a surface picks names
 *  that don't collide with its other actions (and so two composers can coexist). */
export interface RefineComposerActs {
  /** Opens the composer (the hover affordance). */
  open: string
  /** Submits the feedback text → the surface asks its AI seam. */
  submit: string
  /** Closes the composer / discards a proposal. */
  cancel: string
  /** Accepts the AI proposal (HITL). Unused when `proposal` stays null. */
  accept: string
  /** Discards the current proposal to write fresh feedback. Unused when `proposal` stays null. */
  again: string
}

/** The already-`t()`'d labels the composer renders (the surface passes German strings). */
export interface RefineComposerLabels {
  /** The affordance label AND the composer's own heading (»passt nicht ganz? …«). */
  open: string
  /** Placeholder for the feedback textarea. */
  placeholder: string
  /** The submit button in its rest / pending states. */
  submit: string
  submitting: string
  cancel: string
  /** The proposal block's label + its three actions (only rendered in state 3). */
  proposalLabel: string
  accept: string
  again: string
  discard: string
}

/** The composer's current state — owned by the driving surface's signals. */
export interface RefineComposerState {
  /** Whether this composer is expanded. */
  open: boolean
  /** The AI proposal awaiting HITL accept — null while collecting feedback (or always, for
   *  a surface that re-derives wholesale rather than proposing a replacement text). */
  proposal: string | null
  /** An AI call is in flight (disables submit/accept and shows the pending label). */
  busy: boolean
  /** Stamped as `data-block` on every action so a multi-composer surface can route the pick. */
  block?: string
}

/** The spark glyph on the affordance — mirrors the werte-sektion »Feedback« icon. */
const SPARK = html`<svg class="rc-spark" viewBox="0 0 24 24" aria-hidden="true">
  <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M18 6l-2.5 2.5M8.5 15.5 6 18" />
</svg>`

/**
 * Read the trimmed value of the composer's feedback field from its container. The surface
 * passes the acting composer root (`event.target.closest(".rc-composer")`) — the same
 * DOM-read idiom the werte-sektion runner uses. `.rc-feedback` is the stable read hook.
 */
export function readRefineFeedback(root: Element | null | undefined): string {
  return root?.querySelector<HTMLTextAreaElement>("textarea.rc-feedback")?.value.trim() ?? ""
}

/**
 * Render the composer for the given state. Returns the hover affordance alone when closed,
 * or the affordance followed by the expanded panel when open.
 */
export function refineComposer(
  state: RefineComposerState,
  acts: RefineComposerActs,
  labels: RefineComposerLabels,
): TemplateResult {
  const block = state.block ?? nothing
  const affordance = html`<div class="ws-afford rc-afford">
    <button class="ws-iconbtn" data-act=${acts.open} data-block=${block} lang="de" aria-label=${labels.open}>
      ${SPARK}<span>${labels.open}</span>
    </button>
  </div>`
  if (!state.open) return affordance
  return html`${affordance}${panel(state, acts, labels, block)}`
}

/** The expanded composer — the feedback textarea, or the proposal awaiting accept. */
function panel(
  state: RefineComposerState,
  acts: RefineComposerActs,
  labels: RefineComposerLabels,
  block: string | typeof nothing,
): TemplateResult {
  if (state.proposal === null) {
    return html`<div class="ws-refine rc-composer" data-block=${block}>
      <p class="ws-refine-label" lang="de">${labels.open}</p>
      <textarea
        class="rlhf-seam rc-feedback"
        rows="1"
        ${seamAutoGrow()}
        placeholder=${labels.placeholder}
        aria-label=${labels.open}
        lang="de"
      ></textarea>
      <div class="ws-refine-actions">
        <button
          class=${classMap({ "ws-btn-sm": true, "is-primary": true, "is-pending": state.busy })}
          data-act=${acts.submit}
          data-block=${block}
          ?disabled=${state.busy}
          lang="de"
        >${state.busy ? labels.submitting : labels.submit}</button>
        <button class="ws-btn-sm" data-act=${acts.cancel} lang="de">${labels.cancel}</button>
      </div>
    </div>`
  }
  return html`<div class="ws-refine rc-composer" data-block=${block}>
    <div class="ws-proposal">
      <p class="ws-proposal-label" lang="de">${labels.proposalLabel}</p>
      <p class="ws-proposal-text" lang="de">${state.proposal}</p>
      <div class="ws-proposal-actions">
        <button class="ws-btn-sm is-primary" data-act=${acts.accept} data-block=${block} ?disabled=${state.busy} lang="de">${labels.accept}</button>
        <button class="ws-btn-sm" data-act=${acts.again} lang="de">${labels.again}</button>
        <button class="ws-btn-sm" data-act=${acts.cancel} lang="de">${labels.discard}</button>
      </div>
    </div>
  </div>`
}
